import { CollectionType, type OrderFormInfoData, Sex } from '@/types/RequisitionFormTypes'

type BirthDateInput = Date | string | null | undefined

interface CalendarDateParts {
  year: number
  month: number
  day: number
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

function parseDateOnly(value: string): CalendarDateParts | null {
  const match = value.trim().match(DATE_ONLY_PATTERN)
  if (!match) {
    return null
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const localDate = new Date(year, month - 1, day)
  if (localDate.getFullYear() !== year || localDate.getMonth() !== month - 1 || localDate.getDate() !== day) {
    return null
  }

  return { year, month, day }
}

/**
 * Extract calendar date parts from a Date for DOB display/calculation.
 * ISO date-only strings (e.g. "2026-05-01") parse as UTC midnight; use UTC components
 * so the intended calendar day is preserved in all timezones.
 * Dates created via `new Date(y, m, d)` use local components.
 */
function getCalendarPartsFromDate(date: Date): CalendarDateParts {
  const isUtcMidnight =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0

  if (isUtcMidnight) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    }
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  }
}

function formatCalendarParts(parts: CalendarDateParts): string {
  return `${String(parts.month).padStart(2, '0')}/${String(parts.day).padStart(2, '0')}/${parts.year}`
}

/** Local calendar date as ISO `YYYY-MM-DD` (for attestation/signature dates). */
export function getLocalCalendarDateString(d: Date = new Date()): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseDateOnlyToLocalDate(value: string): Date | null {
  const parts = parseDateOnly(value)
  if (!parts) {
    return null
  }
  return new Date(parts.year, parts.month - 1, parts.day)
}

function resolveBirthDate(value: BirthDateInput): Date | null {
  if (!value) {
    return null
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null
    }
    const parts = getCalendarPartsFromDate(value)
    return new Date(parts.year, parts.month - 1, parts.day)
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  const dateOnly = parseDateOnlyToLocalDate(trimmed)
  if (dateOnly || DATE_ONLY_PATTERN.test(trimmed)) {
    return dateOnly
  }

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Format a birth date for display (en-US, MM/DD/YYYY).
 */
export function formatBirthDate(birthDate: BirthDateInput): string {
  if (typeof birthDate === 'string') {
    const parts = parseDateOnly(birthDate)
    if (parts) {
      return formatCalendarParts(parts)
    }
  }

  const resolved = resolveBirthDate(birthDate)
  if (!resolved) {
    return ''
  }
  return formatCalendarParts(getCalendarPartsFromDate(resolved))
}

/**
 * Format age from birth date (e.g. "5 days old", "3 months old", "2 years old").
 */
export function formatAge(birthDate: BirthDateInput): string {
  const resolved = resolveBirthDate(birthDate)
  if (!resolved) {
    return ''
  }
  const today = new Date()
  const diffMs = today.getTime() - resolved.getTime()
  if (diffMs < 0) {
    return ''
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} old`
  }

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 24) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} old`
  }

  const diffYears = Math.floor(diffDays / 365)
  return `${diffYears} year${diffYears === 1 ? '' : 's'} old`
}

/**
 * Format medical record number for patient banner UI: "MRN: {id}".
 * Strips a leading "MRN" prefix (optional colon and spaces) when present so values like "MRN400663" normalize correctly.
 */
/** Map patient sex enum to a short label for order banner and similar UI. */
export function formatSexForDisplay(sex: Sex | undefined | null): string {
  if (sex === Sex.Male) {
    return 'Male'
  }
  if (sex === Sex.Female) {
    return 'Female'
  }
  if (sex === Sex.Ambiguous) {
    return 'Ambiguous'
  }
  return ''
}

export function formatMrnForDisplay(mrn: string | undefined | null): string {
  const trimmed = mrn?.trim() ?? ''
  if (!trimmed) {
    return ''
  }
  const withoutPrefix = trimmed.replace(/^mrn\s*:?\s*/i, '').trim()
  const id = withoutPrefix || trimmed
  return `MRN: ${id}`
}

/**
 * The raw MRN identifier for click-to-copy (no ``MRN:`` presentation prefix).
 * Returns an empty string when there is no real value, so callers can gate the
 * copy affordance off placeholder/empty states.
 */
export function rawMrnValue(mrn: string | undefined | null): string {
  const trimmed = mrn?.trim() ?? ''
  if (!trimmed) {
    return ''
  }
  const withoutPrefix = trimmed.replace(/^mrn\s*:?\s*/i, '').trim()
  return withoutPrefix || trimmed
}

/** Sample type string used in form state and UI (maps to CollectionType). */
export type SampleTypeOption = 'whole-blood' | 'buccal-swab' | 'saliva'

/**
 * Map CollectionType enum to display sample type for order cards and modals.
 */
/** IMDB reads back some collection types by specimen-type name (``Buccal``, ``Saliva``), so matching ignores case. */
export function mapCollectionTypeToSampleType(collectionType?: CollectionType): SampleTypeOption {
  return mapCollectionStringToSampleTypeStrict(collectionType) ?? 'whole-blood'
}

/**
 * Strict mapping for **configuration** strings (e.g. ``UiConfig.testTypes``):
 * returns ``null`` for unknown values instead of coercing to ``whole-blood``.
 */
export function mapCollectionStringToSampleTypeStrict(value: string | null | undefined): SampleTypeOption | null {
  const normalized = (value ?? '').trim().toLowerCase()
  if (normalized === CollectionType.Buccal) return 'buccal-swab'
  if (normalized === CollectionType.WholeBloodEDTA) return 'whole-blood'
  if (normalized === CollectionType.Saliva) return 'saliva'
  return null
}

/** Display label for {@link SampleTypeOption} (shared by proband + parent modals). */
export function getSampleTypeLabel(value: SampleTypeOption): string {
  if (value === 'buccal-swab') return 'Buccal Swab'
  if (value === 'saliva') return 'Saliva'
  return 'Whole Blood'
}

/**
 * Map sample type string back to CollectionType for form/API.
 */
export function mapSampleTypeToCollectionType(sampleType?: SampleTypeOption): CollectionType {
  if (sampleType === 'buccal-swab') {
    return CollectionType.Buccal
  }
  if (sampleType === 'whole-blood') {
    return CollectionType.WholeBloodEDTA
  }
  if (sampleType === 'saliva') {
    return CollectionType.Saliva
  }
  return CollectionType.Unknown
}

/** HPO category string from form/API. */
export type HPOCategoryKey = 'Key' | 'Additional' | 'Excluded'

/** Normalized category type used by IndicationsCard and related components. */
export type HPOCategoryType = 'key' | 'additional' | 'excluded'

/**
 * Map HPO category label to normalized type for indications display.
 */
export function mapCategoryToType(category?: HPOCategoryKey): HPOCategoryType {
  if (category === 'Key') {
    return 'key'
  }
  if (category === 'Additional') {
    return 'additional'
  }
  return 'excluded'
}

/**
 * Inverse of {@link mapCategoryToType}: map a normalized indication type back to
 * its canonical HPO category label.
 */
export function mapTypeToCategory(type?: HPOCategoryType): HPOCategoryKey {
  if (type === 'key') {
    return 'Key'
  }
  if (type === 'additional') {
    return 'Additional'
  }
  return 'Excluded'
}

/** Family relative groups flagged as presenting with key phenotypes. */
export interface FamilyMembersWithPhenotypes {
  parent?: boolean | null
  grandparent?: boolean | null
  sibling?: boolean | null
  cousin?: boolean | null
}

/**
 * Build the "… present with key phenotypes." supporting copy shown on the
 * family-history card from the set of flagged relative groups. Returns
 * `undefined` when no relative group is flagged.
 */
export function formatFamilyHistoryDetails(members: FamilyMembersWithPhenotypes): string | undefined {
  const labels: string[] = []
  if (members.parent) labels.push('Parent(s)')
  if (members.grandparent) labels.push('Grandparent(s)')
  if (members.sibling) labels.push('Sibling(s)')
  if (members.cousin) labels.push('Cousin(s)')
  if (labels.length === 0) {
    return undefined
  }
  if (labels.length === 1) {
    return `${labels[0]} present with key phenotypes.`
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]} present with key phenotypes.`
  }
  const last = labels[labels.length - 1]
  const rest = labels.slice(0, -1)
  return `${rest.join(', ')}, and ${last} present with key phenotypes.`
}

/**
 * Derive the patient demographics summary (name, MRN, age, DOB, optional sex)
 * shown in the order header from order form info. Returns empty strings when
 * no patient is present so the UI can render placeholders.
 */
export const getPatientInfoFromForm = (data: OrderFormInfoData | null) => {
  if (!data?.patient) {
    return {
      name: '',
      mrn: '',
      age: '',
      dob: '',
    }
  }

  const { firstName, middleName, lastName } = data.patient.name
  const givenNames = [firstName, middleName].filter(Boolean).join(' ')
  const fullName = lastName && givenNames ? `${lastName}, ${givenNames}` : lastName || givenNames || ''

  const sexLabel = formatSexForDisplay(data.patient.sex)

  return {
    name: fullName,
    mrn: data.patient.mrn || '',
    age: formatAge(data.patient.birthDate),
    dob: formatBirthDate(data.patient.birthDate),
    ...(sexLabel ? { sex: sexLabel } : {}),
  }
}
